/**
 * Tenant AI knowledge base — entries that feed the bot's `{{knowledgeBase}}` at
 * runtime. Proxied through operations at `/api/messaging/ai-knowledge`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/ai-knowledge';

export const KNOWLEDGE_CATEGORIES = ['pricing', 'faq', 'service_info', 'policy', 'custom'] as const;
export type KnowledgeCategory = (typeof KNOWLEDGE_CATEGORIES)[number];

export const KNOWLEDGE_CATEGORY_LABELS: Record<KnowledgeCategory, string> = {
  pricing: 'Pricing',
  faq: 'FAQ',
  service_info: 'Service info',
  policy: 'Policy',
  custom: 'Custom',
};

export interface KnowledgeEntry {
  id: string;
  title: string;
  category: KnowledgeCategory;
  content: string;
  enabled: boolean;
  sortOrder: number;
}

export interface CreateKnowledgePayload {
  title: string;
  category: KnowledgeCategory;
  content: string;
  enabled?: boolean;
}

export interface UpdateKnowledgePayload {
  title?: string;
  category?: KnowledgeCategory;
  content?: string;
  enabled?: boolean;
}

export const aiKnowledgeKey = ['messaging', 'ai-knowledge'] as const;

export function useKnowledge() {
  return useQuery({
    queryKey: aiKnowledgeKey,
    queryFn: () => apiGet<KnowledgeEntry[]>(operationsClient, BASE),
    staleTime: 30_000,
  });
}

export function useCreateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateKnowledgePayload) =>
      apiPost<KnowledgeEntry, CreateKnowledgePayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKnowledgeKey });
    },
  });
}

export function useUpdateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateKnowledgePayload) =>
      apiPut<KnowledgeEntry, UpdateKnowledgePayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKnowledgeKey });
    },
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKnowledgeKey });
    },
  });
}
